/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonButtons, IonChip, IonFab, IonFabButton, IonMenuButton } from '@ionic/react';
import classNames from 'classnames';

import { Business, BusinessTypeEnum, EmptyBusiness } from 'model';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useCreateBusinessMutation, useGetTopNQuery, useLazySearchBusinessQuery } from '../api/BusinessApi';
import { BusinessItem } from '../components/BusinessItem';
import { SearchBar } from '../components/Forms/SearchBar';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { NoResults } from '../components/NoResults';
import {
	IconAdd,
	IconBusiness,
	IconCare,
	IconFix,
	IconGiving,
	IconGlobe,
	IconGroceries,
	IconLearn,
	IconRestaurant,
	IconShopping
} from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { QuirkLoader } from '../components/QuirkLoader';
import { QuirkVirtualList } from '../components/QuirkVirtualList';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useLoggedIn } from '../hooks/useLoggedIn';
import { useQueryWithDebounce } from '../hooks/useQueryWithDebounce';
import { useUserHasRole } from '../hooks/useUserHasRole';
import { isMutationError } from '../util/error';
import styles from './BusinessSearch.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import sharedStyles from '../styles/Shared.module.css';

function BusinessListItem({ item }: { item: Business }) {
	return <BusinessItem key={item._id} business={item} />;
}

export const BusinessSearch = () => {
	const user = useCurrentUser();
	const isAdmin = useUserHasRole('admin');
	const [businessType, setBusinessType] = useState('all');
	const [search, setSearch] = useState('');
	// const [searching, setSearching] = useState(false);
	const history = useHistory();
	const {
		trigger,
		data: searchData,
		isFetching: searchFetching,
		isLoading: searchLoading,
		isSuccess: searchSuccess,
		isError: searchError
	} = useQueryWithDebounce(useLazySearchBusinessQuery, 300);
	const { data: topn, isLoading: topNLoading } = useGetTopNQuery(300);
	const data = search === '' && businessType === 'all' ? topn : searchData;

	const filters: { label: string; type: string; icon: JSX.Element; className?: string }[] = [
		{ label: 'All', type: 'all', icon: <IconGlobe size="small" /> },
		{ label: 'Eat', type: 'fooddrink', icon: <IconRestaurant size="small" /> },
		{ label: 'Groceries', type: 'grocery', icon: <IconGroceries size="small" /> },
		{ label: 'Fix', type: 'service', icon: <IconFix size="small" /> },
		{ label: 'Shop', type: 'retail', icon: <IconShopping size="small" /> },
		{ label: 'Care', type: 'personal', icon: <IconCare size="small" /> },
		{ label: 'Give', type: 'giving', icon: <IconGiving size="small" /> },
		{ label: 'Learn', type: 'education', icon: <IconLearn size="small" /> }
	];

	// This handles if the hash sets the search terms, in this cases businessType and search will
	// empty, and we will trigger a search and set the business type and search
	useEffect(() => {
		if (businessType === 'all' && search === '') {
			const hash = window.location.hash;
			if (hash && hash.length > 1) {
				const [type, searchStr] = decodeURI(hash).replace('#', '').split('-');
				const safeType = BusinessTypeEnum.safeParse(type);
				if ((safeType.success || type === 'all') && searchStr !== search) {
					setBusinessType(type);
					setSearch(searchStr);
					trigger({ search: searchStr, businessType: type });
				}
			}
		}
	}, [trigger, businessType, search]);

	function handleChip(type: string) {
		setBusinessType(type);
		window.location.hash = `${type}-${search}`;
		trigger({ search, businessType: type });
	}

	const [createBusiness, { isLoading: updateIsLoading }] = useCreateBusinessMutation();
	const handleCreateClick = async () => {
		if (updateIsLoading) {
			return;
		}
		// add attributes for user suggested when isAdmin === false
		const business: Business = isAdmin
			? { ...EmptyBusiness, draft: true }
			: { ...EmptyBusiness, draft: true, submittedBy: user._id, submissionStatus: 'pending' };
		const result = await createBusiness({ business });
		if (!isMutationError(result)) {
			history.push(`/main/bus/edit/${result.data._id}`);
		}
	};
	const loggedIn = useLoggedIn();
	const createBusinessItem = (
		<div className={styles.searchListHeader}>
			<IconButton
				icon={
					<span className={styles.iconWrapper}>
						<IconAdd />
					</span>
				}
				label={`${isAdmin ? 'Create' : 'Suggest a '} new business...`}
				onClick={handleCreateClick}
			/>
		</div>
	);
	const items = loggedIn.realUser ? (data === undefined ? undefined : [createBusinessItem, ...data]) : data;

	return (
		<QuirkIonPage roles={['anonymous', 'user']} name="Business Directory">
			<Header
				title="Explore Local Businesses"
				left={
					<IonButtons>
						<IonMenuButton />
					</IonButtons>
				}
			/>

			<QuirkIonContent scroll={false}>
				<>
					<div className={sharedStyles.searchContainer} style={{ paddingTop: 10 }}>
						<SearchBar
							placeholder="Search for businesses"
							value={search}
							isFetching={searchFetching}
							isLoading={searchLoading}
							onChangeAction={event => {
								history.replace(`/main/bus/#${businessType}-${event.detail.value}`);
								setSearch(event.detail.value as string);
								trigger({ search: event.detail.value ? event.detail.value : '', businessType });
							}}
						/>
					</div>

					<div className={styles.chipHScroll}>
						{filters.map(filter => {
							return (
								<IonChip
									className={classNames(styles.chip, filter.className ?? '')}
									onClick={() => handleChip(filter.type)}
									key={filter.label}
									outline={businessType !== filter.type}
									color="primary"
								>
									<div className={styles.chipIcon}>{filter.icon}</div>

									<span className={styles.filterLabel}>{filter.label}</span>
								</IonChip>
							);
						})}
					</div>
					{loggedIn.realUser && (
						<div className="ionFabContainer">
							<IonFab vertical="bottom" horizontal="end">
								<IonFabButton
									color="secondary"
									className="IonFabButton"
									onClick={handleCreateClick}
									disabled={updateIsLoading}
								>
									<IconAdd size="xlarge" />
								</IonFabButton>
							</IonFab>
						</div>
					)}
					<QuirkIonContent flush>
						<QuirkLoader
							isLoading={topNLoading}
							loadingMessage="Loading businesses..."
							data={!searchSuccess && !searchError ? [1] : data}
							noResults={
								<NoResults
									heading="No businesses found."
									subheading="Refine or clear your search filters."
									icon={<IconBusiness />}
								>
									{createBusinessItem}
								</NoResults>
							}
						>
							<QuirkVirtualList testId="business-list" items={items} ItemComponent={BusinessListItem} />
						</QuirkLoader>
					</QuirkIonContent>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
